<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <!-- Utbetalningsdatum -->
      <div :id="section.subSections.date.id">
        <h3 class="mb-2">
          {{ section.subSections.date.title }}
        </h3>

        <AppTable v-if="payoutDetails.payoutDate != null || payoutDetails.payoutDateASAP || finalPayoutDate != null">
          <thead>
            <tr>
              <th>{{ isNewLoan ? 'Tillträdesdag' : 'Önskat utbetalningsdatum' }}</th>
              <th v-if="isNewLoan">Tillträdestid</th>
              <th>Planerad utbetalning</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow>
              <AppTableCell>
                <template v-if="payoutDetails.payoutDateASAP">Så fort som möjligt</template>
                <template v-else>{{ payoutDay }}</template>
              </AppTableCell>
              <AppTableCell v-if="isNewLoan">{{ payoutTime ?? '-' }}</AppTableCell>
              <AppTableCell>
                {{ formatDateTimeValue(finalPayoutDate) }}
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inget utbetalningsdatum angivet.</p>
      </div>

      <!-- Utbetalningsdetaljer -->
      <div :id="section.subSections.details.id">
        <h3 class="mb-2">
          {{ section.subSections.details.title }}
        </h3>

        <AppTable v-if="payoutDetails.details.length">
          <thead>
            <tr>
              <th>Typ</th>
              <th>Mottagare</th>
              <th>Kontonummer</th>
              <th>Belopp</th>
              <th>Referens</th>
              <th>Från konto</th>
              <th>Kommentar</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="(detail, index) in payoutDetails.details" :key="index">
              <AppTableCell>{{ $t(`PAYOUT_TYPE.${detail.type}`) }}</AppTableCell>
              <AppTableCell>{{ detail.bank ?? '-' }}</AppTableCell>
              <AppTableCell>{{ detail.bankAccount ?? '-' }}</AppTableCell>
              <AppTableCell>{{ formatAmountWithCurrency(detail.amount) }}</AppTableCell>
              <AppTableCell>{{ detail.reference ?? '-' }}</AppTableCell>
              <AppTableCell>{{ detail.source ? $t(`PAYOUT_SOURCE.${detail.source}`) : '-' }}</AppTableCell>
              <AppTableCell>{{ detail.comment ?? '-' }}</AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inga utbetalningsdetaljer angivna.</p>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import { SectionCard, AppTable, AppTableRow, AppTableCell } from '@staff/ui'
import { formatAmountWithCurrency, formatDateTimeValue } from '@staff/shared'
import { useRequiredApplication } from '@/views/application/application.context'
import type { ApplicationSections } from './sections'

defineProps<{
  section: ApplicationSections['payout']
}>()

const application = useRequiredApplication()

const isNewLoan = computed(() => application.value.type === 'newLoanApplication')
const payoutDetails = computed(() => application.value.plannedPayoutDetails)
const finalPayoutDate = computed(() => application.value.finalPayoutDate)

const payoutDay = computed(() => {
  const date = payoutDetails.value.payoutDate
  if (!date) return '-'
  return dayjs(date).format('YYYY-MM-DD')
})

const payoutTime = computed(() => {
  const date = payoutDetails.value.payoutDate
  if (!date) return undefined
  const time = dayjs(date).format('HH:mm')
  return time !== '00:00' ? time : undefined
})
</script>

<style scoped lang="scss"></style>
